import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Zap,
  Target,
  TrendingUp,
  Sparkles,
  GraduationCap,
  Video,
  Trophy,
  Users,
  ChevronRight,
  CheckCircle2,
  DollarSign,
  BarChart3,
  Globe,
  Tv,
  Play,
  Award,
  Shield,
  Handshake,
  Mail,
  Phone,
  Building2,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PartnerWithUs = () => {
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    organization_name: '', partner_type: '', description: '',
  });

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Handle hash navigation for scrolling to sections
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/partner-applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Submission failed');
      toast.success('Thank you for your interest! Our team will contact you within 24 hours.');
      setForm({ first_name: '', last_name: '', email: '', phone: '', organization_name: '', partner_type: '', description: '' });
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const partnerBenefits = [
    {
      icon: DollarSign,
      title: 'Revenue Sharing',
      description: 'Earn from ad revenue generated from your streams',
      color: 'text-green-400',
      bgColor: 'bg-green-500/20',
      borderColor: 'border-green-500',
    },
    {
      icon: BarChart3,
      title: 'Analytics Dashboard',
      description: 'Real-time insights on viewership, engagement, and earnings',
      color: 'text-rooam-blue',
      bgColor: 'bg-rooam-blue/20',
      borderColor: 'border-rooam-blue',
    },
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'Connect families and fans worldwide with your events',
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/20',
      borderColor: 'border-purple-500',
    },
    {
      icon: Tv,
      title: 'Professional Production',
      description: 'Access to streaming equipment and production support',
      color: 'text-rooam-red',
      bgColor: 'bg-rooam-red/20',
      borderColor: 'border-rooam-red',
    },
  ];

  const partnerTypes = [
    {
      id: 'schools',
      icon: GraduationCap,
      title: 'Schools & Athletic Programs',
      description: 'Stream school sports and generate revenue for your athletic programs while keeping families connected.',
      color: 'text-rooam-blue',
      bgColor: 'bg-rooam-blue/20',
      features: [
        'Dedicated streaming channel for your school',
        'Revenue share for athletic department funding',
        'Parent engagement tools and notifications',
        'Archive and replay access for athletes',
        'College recruitment exposure',
      ],
    },
    {
      id: 'creators',
      icon: Video,
      title: 'Content Creators',
      description: 'Monetize your sports content with our dynamic ad player technology and reach a passionate audience.',
      color: 'text-rooam-red',
      bgColor: 'bg-rooam-red/20',
      features: [
        'Dynamic ad insertion technology',
        'Multiple monetization options',
        'Creator tools and analytics',
        'Highlight reel generation',
        'Brand partnership opportunities',
      ],
    },
    {
      id: 'tournaments',
      icon: Trophy,
      title: 'Tournament Directors',
      description: 'Broadcast your tournaments to a wider audience and earn from every stream while enhancing the experience.',
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/20',
      features: [
        'Multi-court streaming capability',
        'Bracket integration and live scores',
        'Spectator engagement features',
        'Premium tier options for venues',
        'Sponsorship integration',
      ],
    },
    {
      id: 'leagues',
      icon: Users,
      title: 'Leagues & Organizations',
      description: 'Expand your reach and unlock new revenue streams while providing value to your member teams.',
      color: 'text-green-400',
      bgColor: 'bg-green-500/20',
      features: [
        'League-wide streaming platform',
        'Centralized management dashboard',
        'Team and player statistics',
        'Recruitment network access',
        'Custom branding options',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-rooam-black">
      {/* Hero Section */}
      <section className="relative py-20 px-4 bg-gradient-to-b from-rooam-gray-dark to-rooam-black overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-rooam-blue/10 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-12">
            <Badge className="bg-rooam-blue/20 text-rooam-blue border-rooam-blue mb-4">
              <Handshake className="h-3 w-3 mr-1" />
              Partnership Program
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Partner With <span className="text-rooam-blue">Rooam Sports</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Join the fastest-growing sports streaming network. We work with schools, creators, tournament directors, and leagues to bring sports to a wider audience with unmatched revenue generation.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" className="bg-rooam-blue hover:bg-rooam-blue-light text-black font-bold" asChild>
                <a href="#contact">
                  Apply Now
                  <ChevronRight className="h-5 w-5 ml-1" />
                </a>
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-black" asChild>
                <a href="#benefits">
                  Learn More
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Revenue Generation Section (Moved from Home) */}
      <section id="benefits" className="py-16 px-4 bg-rooam-black">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-rooam-blue bg-opacity-20 text-rooam-blue border border-rooam-blue hover:bg-rooam-blue hover:bg-opacity-20 mb-4">
                <Zap className="h-3 w-3 mr-1" />
                Revenue Generation
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Monetize Your Sports Content With Our Platform
              </h2>
              <p className="text-lg text-gray-300 mb-6">
                Turn your sports content into a revenue stream. Our platform seamlessly integrates sponsored content during live streams and replays, helping you earn while delivering great viewing experiences.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="bg-rooam-blue bg-opacity-20 p-2 rounded-lg border border-rooam-blue">
                    <Target className="h-5 w-5 text-rooam-blue" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Smart Ad Placement</h4>
                    <p className="text-sm text-gray-400">
                      Intelligent ad placement that doesn't interrupt the game experience
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-rooam-red bg-opacity-20 p-2 rounded-lg border border-rooam-red">
                    <TrendingUp className="h-5 w-5 text-rooam-red" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Performance Dashboard</h4>
                    <p className="text-sm text-gray-400">
                      Track your streams and see how your content performs
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-rooam-blue bg-opacity-20 p-2 rounded-lg border border-rooam-blue">
                    <Sparkles className="h-5 w-5 text-rooam-blue" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Seamless Integration</h4>
                    <p className="text-sm text-gray-400">
                      Non-intrusive experience that keeps viewers engaged
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <Card className="bg-rooam-gray-medium border-rooam-gray-light shadow-2xl shadow-rooam-blue/10">
                <CardContent className="p-6 sm:p-8">
                  <div className="text-center mb-8">
                    <div className="text-4xl sm:text-5xl font-bold text-rooam-blue mb-2">Partner Benefits</div>
                    <p className="text-gray-400">What you get when you join</p>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-rooam-black bg-opacity-50 p-4 rounded-lg border border-rooam-gray-light flex items-center gap-4">
                      <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                        <DollarSign className="h-5 w-5 text-green-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-white">Revenue Sharing</h4>
                        <p className="text-sm text-gray-400">Earn from every stream</p>
                      </div>
                    </div>
                    <div className="bg-rooam-black bg-opacity-50 p-4 rounded-lg border border-rooam-gray-light flex items-center gap-4">
                      <div className="w-10 h-10 bg-rooam-blue/20 rounded-full flex items-center justify-center">
                        <BarChart3 className="h-5 w-5 text-rooam-blue" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-white">Analytics Dashboard</h4>
                        <p className="text-sm text-gray-400">Track your performance</p>
                      </div>
                    </div>
                    <div className="bg-rooam-black bg-opacity-50 p-4 rounded-lg border border-rooam-gray-light flex items-center gap-4">
                      <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                        <Globe className="h-5 w-5 text-purple-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-white">Wider Reach</h4>
                        <p className="text-sm text-gray-400">Connect with more fans</p>
                      </div>
                    </div>
                    <div className="bg-rooam-black bg-opacity-50 p-4 rounded-lg border border-rooam-gray-light flex items-center gap-4">
                      <div className="w-10 h-10 bg-rooam-red/20 rounded-full flex items-center justify-center">
                        <Tv className="h-5 w-5 text-rooam-red" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-white">Production Support</h4>
                        <p className="text-sm text-gray-400">Equipment & training included</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Benefits */}
      <section className="py-16 px-4 bg-gradient-to-b from-rooam-black to-rooam-gray-dark">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Why Partner With Us?
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Get everything you need to stream, monetize, and grow your sports content
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnerBenefits.map((benefit) => (
              <Card key={benefit.title} className="bg-rooam-gray-medium border-rooam-gray-light hover:border-rooam-blue transition-colors">
                <CardContent className="p-6">
                  <div className={`w-12 h-12 ${benefit.bgColor} rounded-lg flex items-center justify-center mb-4 border ${benefit.borderColor}`}>
                    <benefit.icon className={`h-6 w-6 ${benefit.color}`} />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{benefit.title}</h3>
                  <p className="text-gray-400 text-sm">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-16 px-4 bg-rooam-gray-dark">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Partnership Programs
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Choose the program that fits your organization
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            {partnerTypes.map((partner) => (
              <Card 
                key={partner.id} 
                id={partner.id}
                className="bg-rooam-gray-medium border-rooam-gray-light hover:border-rooam-blue transition-colors scroll-mt-24"
              >
                <CardContent className="p-6 sm:p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className={`w-14 h-14 ${partner.bgColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <partner.icon className={`h-7 w-7 ${partner.color}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{partner.title}</h3>
                      <p className="text-gray-400">{partner.description}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {partner.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <CheckCircle2 className={`h-5 w-5 ${partner.color} flex-shrink-0`} />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Button className="w-full mt-6 bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold" asChild>
                    <a href="#contact">
                      Apply as {partner.title.split(' ')[0]}
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 bg-rooam-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Get started in just a few simple steps
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Apply', description: 'Fill out our partnership application form', icon: Mail },
              { step: '02', title: 'Onboarding', description: 'Our team will set up your account and provide training', icon: Shield },
              { step: '03', title: 'Stream', description: 'Start streaming your events with our platform', icon: Play },
              { step: '04', title: 'Earn', description: 'Generate revenue from every stream', icon: DollarSign },
            ].map((item, index) => (
              <div key={item.step} className="relative">
                <div className="text-center">
                  <div className="text-6xl font-bold text-rooam-gray-light mb-4">{item.step}</div>
                  <div className="w-16 h-16 bg-rooam-blue/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-rooam-blue">
                    <item.icon className="h-8 w-8 text-rooam-blue" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/4 right-0 transform translate-x-1/2">
                    <ChevronRight className="h-8 w-8 text-rooam-gray-light" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-16 px-4 bg-gradient-to-b from-rooam-black to-rooam-gray-dark scroll-mt-20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-rooam-blue/20 text-rooam-blue border-rooam-blue mb-4">
              <Handshake className="h-3 w-3 mr-1" />
              Get Started
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Apply to Become a Partner
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Fill out the form below and our partnership team will contact you within 24 hours
            </p>
          </div>
          
          <Card className="bg-rooam-gray-medium border-rooam-gray-light">
            <CardContent className="p-6 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">First Name *</label>
                    <Input 
                      type="text" 
                      required
                      placeholder="John"
                      value={form.first_name}
                      onChange={e => updateField('first_name', e.target.value)}
                      data-testid="partner-first-name"
                      className="bg-rooam-black border-rooam-gray-light text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Last Name *</label>
                    <Input 
                      type="text" 
                      required
                      placeholder="Smith"
                      value={form.last_name}
                      onChange={e => updateField('last_name', e.target.value)}
                      data-testid="partner-last-name"
                      className="bg-rooam-black border-rooam-gray-light text-white"
                    />
                  </div>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Email *</label>
                    <Input 
                      type="email" 
                      required
                      placeholder="john@example.com"
                      value={form.email}
                      onChange={e => updateField('email', e.target.value)}
                      data-testid="partner-email"
                      className="bg-rooam-black border-rooam-gray-light text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
                    <Input 
                      type="tel" 
                      placeholder="(555) 123-4567"
                      value={form.phone}
                      onChange={e => updateField('phone', e.target.value)}
                      data-testid="partner-phone"
                      className="bg-rooam-black border-rooam-gray-light text-white"
                    />
                  </div>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Organization Name *</label>
                    <Input 
                      type="text" 
                      required
                      placeholder="Your Organization"
                      value={form.organization_name}
                      onChange={e => updateField('organization_name', e.target.value)}
                      data-testid="partner-org-name"
                      className="bg-rooam-black border-rooam-gray-light text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Partner Type *</label>
                    <select 
                      required
                      value={form.partner_type}
                      onChange={e => updateField('partner_type', e.target.value)}
                      data-testid="partner-type-select"
                      className="w-full bg-rooam-black border border-rooam-gray-light text-white rounded-md px-3 py-2 appearance-none cursor-pointer [&>option]:bg-gray-900 [&>option]:text-white"
                      style={{ colorScheme: 'dark' }}
                    >
                      <option value="" className="bg-gray-900 text-white">Select partner type</option>
                      <option value="school" className="bg-gray-900 text-white">School / Athletic Program</option>
                      <option value="creator" className="bg-gray-900 text-white">Content Creator</option>
                      <option value="tournament" className="bg-gray-900 text-white">Tournament Director</option>
                      <option value="league" className="bg-gray-900 text-white">League / Organization</option>
                      <option value="other" className="bg-gray-900 text-white">Other</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Tell us about your organization</label>
                  <Textarea 
                    placeholder="Describe your organization, the sports you cover, expected streaming frequency, etc."
                    value={form.description}
                    onChange={e => updateField('description', e.target.value)}
                    data-testid="partner-description"
                    className="bg-rooam-black border-rooam-gray-light text-white min-h-[120px]"
                  />
                </div>
                
                <div className="flex items-start gap-3">
                  <input 
                    type="checkbox" 
                    required
                    className="mt-1 h-4 w-4 rounded border-rooam-gray-light bg-rooam-black text-rooam-blue focus:ring-rooam-blue"
                  />
                  <label className="text-sm text-gray-400">
                    I agree to receive communications from Rooam Sports Network about partnership opportunities. 
                    View our <Link to="/privacy" className="text-rooam-blue hover:underline">Privacy Policy</Link>.
                  </label>
                </div>
                
                <Button type="submit" size="lg" disabled={submitting} data-testid="partner-submit-btn" className="w-full bg-rooam-blue hover:bg-rooam-blue-light text-black font-bold">
                  {submitting ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Submitting...</> : <>Submit Application <ChevronRight className="h-5 w-5 ml-1" /></>}
                </Button>
              </form>
            </CardContent>
          </Card>
          
        </div>
      </section>
    </div>
  );
};

export default PartnerWithUs;
